import { Box, Typography, TextField, Button, CircularProgress, Paper, IconButton } from "@mui/material";
import { useState } from "react";
import axios from "axios";
import { API_URL } from "../services/authService";

const AddStores = (
    { fetchStoresData, setDisplayAddStore }
) => {
    const [formData, setFormData] = useState({
        name: "",
        address: "",
        phone: "",
        email: ""
    });
    const [errors, setErrors] = useState({
        name: "",
        address: "",
        phone: "",
        email: ""
    });
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [loading, setLoading] = useState(false);

    const validateName = (name) => {
        if (!name.trim()) return "Store name is required";
        if (name.length < 3) return "Store name must be at least 3 characters long";
        if (name.length > 50) return "Store name must be less than 50 characters";
        return "";
    };

    const validateAddress = (address) => {
        if (!address.trim()) return "Address is required";
        if (address.length < 10) return "Please enter complete address";
        if (address.length > 200) return "Address is too long";
        return "";
    };

    const validatePhone = (phone) => {
        if (!phone) return "Phone number is required";
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phoneRegex.test(phone)) return "Please enter a valid 10-digit phone number";
        return "";
    };

    const validateEmail = (email) => {
        if (!email) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) return "Please enter a valid email address";
        return "";
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Validate field
        let errorMessage = "";
        switch (field) {
            case "name":
                errorMessage = validateName(value);
                break;
            case "address":
                errorMessage = validateAddress(value);
                break;
            case "phone":
                errorMessage = validatePhone(value);
                break;
            case "email":
                errorMessage = validateEmail(value);
                break;
            default:
                break;
        }

        setErrors(prev => ({
            ...prev,
            [field]: errorMessage
        }));
    };

    const validateForm = () => {
        const newErrors = {
            name: validateName(formData.name),
            address: validateAddress(formData.address),
            phone: validatePhone(formData.phone),
            email: validateEmail(formData.email)
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error !== "");
    };

    const onCreateStore = async () => {
        if (!validateForm()) {
            setErrorMsg("Please fix all errors before submitting");
            return;
        }

        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        try {
            // Get token from localStorage
            const userData = localStorage.getItem('invCreator');
            if (!userData) {
                throw new Error('Authentication token not found');
            }
            const { token } = JSON.parse(userData);

            // Make API call with token
            const response = await axios.post(
                `${API_URL}/create_stores`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            // console.log(response);

            if (response.statusText === "Created") {
                setSuccessMsg(response.data.message);
                // Reset form
                setFormData({
                    name: "",
                    address: "",
                    phone: "",
                    email: ""
                });
                // Refresh stores list
                fetchStoresData();
                setDisplayAddStore(false);
            } else {
                setErrorMsg(response.data.message || "Failed to create store");
            }
        } catch (error) {
            console.error("Error creating store:", error);
            setErrorMsg(error.response?.data?.message || "An error occurred while creating the store");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            width: { xs: "95%", md: "25%" },
            minWidth: "300px",
            display: "flex",
            justifyContent: "center",
            alignItems: { xs: "start", md: "center" },
        }}>
            <Box component={Paper} sx={{ p: 3, width: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-evenly" }}>
                    <Box sx={{width:"95%"}}>
                        <Typography sx={{
                            textAlign: "center",
                            fontSize: { xs: "2rem", md: "3rem" },
                            fontWeight: "bold",
                            mb: 3
                        }}>
                            Add Store
                        </Typography>
                    </Box>
                    <IconButton sx={{ color: "red", width: '2.5rem', height:"2.5rem" }} onClick={()=>setDisplayAddStore(false)}>x</IconButton>
                </Box>

                <TextField
                    fullWidth
                    variant="outlined"
                    label="Store Name"
                    value={formData.name}
                    onChange={handleChange('name')}
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={{ mb: 2 }}
                    inputProps={{
                        maxLength: 50
                    }}
                />

                <TextField
                    fullWidth
                    variant="outlined"
                    label="Address"
                    value={formData.address}
                    onChange={handleChange('address')}
                    error={!!errors.address}
                    helperText={errors.address}
                    sx={{ mb: 2 }}
                    multiline
                    rows={3}
                    inputProps={{
                        maxLength: 200
                    }}
                />

                <TextField
                    fullWidth
                    variant="outlined"
                    label="Phone Number"
                    value={formData.phone}
                    onChange={handleChange('phone')}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    sx={{ mb: 2 }}
                    inputProps={{
                        maxLength: 10
                    }}
                />

                <TextField
                    fullWidth
                    variant="outlined"
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange('email')}
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={{ mb: 2 }}
                />

                {errorMsg && (
                    <Typography color="error" sx={{ textAlign: "center", mb: 2 }}>
                        {errorMsg}
                    </Typography>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={onCreateStore}
                    disabled={loading}
                    sx={{ height: 48 }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Create Store"}
                </Button>

                {successMsg && (
                    <Typography color="success.main" sx={{ textAlign: "center", mt: 2 }}>
                        {successMsg}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default AddStores;